import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
export default function PhysicalCapability() {
  return <section id="physicalcapabilitycomponents" data-nav="light" className="bg-background text-foreground py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Four important components of long-term physical capability
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: Cardiorespiratory Fitness */}
          <Card className="flex flex-col justify-between border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
            <div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-semibold text-foreground">
                  Cardiorespiratory Fitness
                </CardTitle>
                <p className="text-sm font-medium text-primary pt-1">
                  How effectively can your body deliver and use oxygen during exercise?
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Use VO₂ Max assessment to measure aerobic capacity and provide personalised information about exercise response and training intensity.
                </p>
              </CardContent>
            </div>
            <CardFooter className="pt-2">
              <Button asChild variant="outline" size="sm" className="w-full justify-between">
                <Link to="/VO2MaxMetabolicTestingPage">
                  <span>Explore VO₂ Max Testing</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Card 2: Movement & Gait */}
          <Card className="flex flex-col justify-between border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
            <div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-semibold text-foreground">
                  Movement &amp; Gait
                </CardTitle>
                <p className="text-sm font-medium text-primary pt-1">
                  How efficiently and confidently do you move?
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Use objective gait and plantar-pressure analysis to understand walking, loading and movement patterns.
                </p>
              </CardContent>
            </div>
            <CardFooter className="pt-2">
              <Button asChild variant="outline" size="sm" className="w-full justify-between">
                <Link to="/GaitBalanceAssessmentPage">
                  <span>Explore Gait Assessment</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Card 3: Balance */}
          <Card className="flex flex-col justify-between border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
            <div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-semibold text-foreground">
                  Balance
                </CardTitle>
                <p className="text-sm font-medium text-primary pt-1">
                  How well do you control your body during standing and movement?
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Balance is an important component of physical function, particularly as people age. PhysAnalytica can objectively measure centre-of-pressure behaviour and weight distribution during controlled balance tasks.
                </p>
              </CardContent>
            </div>
            {/* No button required for Card 3 */}
            <div className="px-6 pb-6" />
          </Card>

          {/* Card 4: Metabolism */}
          <Card className="flex flex-col justify-between border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
            <div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-semibold text-foreground">
                  Metabolism
                </CardTitle>
                <p className="text-sm font-medium text-primary pt-1">
                  How much energy does your body use at rest?
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Resting Metabolic Rate testing provides an objective measurement of resting energy expenditure and can support more informed nutrition and body-composition planning.
                </p>
              </CardContent>
            </div>
            <CardFooter className="pt-2">
              <Button asChild variant="outline" size="sm" className="w-full justify-between">
                <Link to="/VO2MaxMetabolicTestingPage">
                  <span>Explore RMR Testing</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </section>;
}